import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Star, Eye, TrendingUp, Check, X, Zap, Clock, Calendar, Building2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PriceTag = ({ entry, unit }) => (
  <div className="flex items-baseline gap-2 mb-2 flex-wrap">
    <span className="text-4xl font-bold text-neutral-900">${entry?.amount}</span>
    {entry?.promo_active && entry?.base_amount !== entry?.amount && (
      <span className="text-lg text-neutral-400 line-through">${entry?.base_amount}</span>
    )}
    <span className="text-neutral-500">{unit}</span>
  </div>
);

const Pricing = () => {
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    axios.get(`${API}/pricing`).then(r => setPricing(r.data)).catch(() => setPricing(null));
  }, []);

  // Fallback values match config.py base prices in case the pricing endpoint fails
  const p = pricing || {
    business_weekly: { amount: 10, base_amount: 10, promo_active: false },
    business_monthly: { amount: 30, base_amount: 30, promo_active: false },
    event_weekly: { amount: 10, base_amount: 10, promo_active: false },
    event_until_date: { amount: 25, base_amount: 25, promo_active: false },
  };
  const promoActive = p.business_monthly?.promo_active;
  const promoEnd = p.business_monthly?.promo_end;
  const promoEndLabel = promoEnd ? new Date(promoEnd + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric' }) : 'December 31';

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="pricing-page">
      <Helmet>
        <title>Pricing | Explore MHC</title>
        <meta name="description" content="Affordable featured placement options for businesses and events in Martinsville & Henry County, Virginia." />
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-br from-primary to-primary-dark text-white py-16 sm:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ fontFamily: 'DM Serif Display, serif' }}>
            Get Seen by Your Community
          </h1>
          <p className="text-lg sm:text-xl text-white/80 max-w-2xl mx-auto">
            Affordable featured placements that put your business or event front and center on Explore MHC.
          </p>
          {promoActive && (
            <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-accent text-neutral-900 rounded-full text-sm font-bold" data-testid="pricing-promo-banner">
              <Zap className="w-4 h-4" />
              $5 OFF every plan — through {promoEndLabel}
            </div>
          )}
          <div className="mt-6 max-w-2xl mx-auto text-white/90 text-sm leading-relaxed">
            <p>New business listings automatically receive <strong>their first month featured FREE</strong>. New event listings receive <strong>their first week featured FREE</strong>. The pricing below applies to renewals, extensions, and additional placements.</p>
          </div>
        </div>
      </section>

      {/* What Featured Gets You */}
      <section className="py-16 sm:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-neutral-900 mb-4" style={{ fontFamily: 'DM Serif Display, serif' }}>
            What Does Featured Get You?
          </h2>
          <p className="text-neutral-600 text-center mb-12 max-w-xl mx-auto">
            Featured listings get priority placement and more visibility across the entire platform.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto mb-16">
            <div className="text-center p-6">
              <div className="w-14 h-14 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto mb-4">
                <Star className="w-7 h-7 text-accent" />
              </div>
              <h3 className="font-bold text-lg text-neutral-900 mb-2">Homepage Spotlight</h3>
              <p className="text-neutral-600 text-sm">Your listing appears in the "Featured" section on the homepage, the first thing visitors see.</p>
            </div>
            <div className="text-center p-6">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-7 h-7 text-primary" />
              </div>
              <h3 className="font-bold text-lg text-neutral-900 mb-2">Top of Directory</h3>
              <p className="text-neutral-600 text-sm">Featured listings appear at the top of search results and category pages, above standard listings.</p>
            </div>
            <div className="text-center p-6">
              <div className="w-14 h-14 rounded-2xl bg-secondary/10 flex items-center justify-center mx-auto mb-4">
                <Eye className="w-7 h-7 text-secondary" />
              </div>
              <h3 className="font-bold text-lg text-neutral-900 mb-2">Visual Highlight</h3>
              <p className="text-neutral-600 text-sm">A "Featured" badge makes your listing stand out with a highlighted border and star icon.</p>
            </div>
          </div>

          {/* Comparison Table */}
          <div className="max-w-3xl mx-auto bg-white rounded-2xl border border-neutral-200 overflow-hidden shadow-sm">
            <div className="grid grid-cols-3 bg-neutral-100 border-b border-neutral-200">
              <div className="p-4 font-semibold text-neutral-700">Feature</div>
              <div className="p-4 font-semibold text-neutral-700 text-center">Free Listing</div>
              <div className="p-4 font-semibold text-accent text-center">Featured</div>
            </div>
            {[
              ['Listed in directory', true, true],
              ['Business detail page', true, true],
              ['Contact info & map', true, true],
              ['Social media links', true, true],
              ['Homepage spotlight', false, true],
              ['Top of search results', false, true],
              ['"Featured" badge', false, true],
              ['Priority in category pages', false, true],
            ].map(([feature, free, featured], i) => (
              <div key={i} className={`grid grid-cols-3 ${i % 2 === 0 ? 'bg-white' : 'bg-neutral-50'} border-b border-neutral-100 last:border-0`}>
                <div className="p-4 text-neutral-700 text-sm">{feature}</div>
                <div className="p-4 flex justify-center">
                  {free ? <Check className="w-5 h-5 text-green-500" /> : <X className="w-5 h-5 text-neutral-300" />}
                </div>
                <div className="p-4 flex justify-center">
                  {featured ? <Check className="w-5 h-5 text-accent" /> : <X className="w-5 h-5 text-neutral-300" />}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-16 sm:py-20 bg-white" data-testid="pricing-cards">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">

          {/* Business Pricing */}
          <div className="mb-20">
            <div className="flex items-center justify-center gap-3 mb-3">
              <Building2 className="w-6 h-6 text-primary" />
              <h2 className="text-2xl sm:text-3xl font-bold text-neutral-900" style={{ fontFamily: 'DM Serif Display, serif' }}>
                Featured Business Placement
              </h2>
            </div>
            <p className="text-neutral-600 text-center mb-10 max-w-lg mx-auto">
              Put your business in the spotlight. Great for attracting new customers from the local community.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl mx-auto">
              {/* Weekly */}
              <div className="rounded-2xl border-2 border-neutral-200 p-8 bg-white hover:border-primary/30 transition-colors" data-testid="biz-weekly-plan">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-5 h-5 text-neutral-400" />
                  <span className="text-sm font-medium text-neutral-500 uppercase tracking-wide">Weekly</span>
                </div>
                <PriceTag entry={p.business_weekly} unit="/week" />
                <ul className="text-neutral-600 text-sm mb-6 space-y-2">
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-0.5 shrink-0" /> Homepage featured spotlight for 7 days</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-0.5 shrink-0" /> Top placement in directory search</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-0.5 shrink-0" /> "Featured" badge on your listing</li>
                </ul>
                <p className="text-xs text-neutral-500 mb-6">Perfect for trying it out or promoting a short-term special.</p>
                <Link to="/register" className="block w-full text-center py-3 rounded-xl border-2 border-primary text-primary font-semibold hover:bg-primary hover:text-white transition-colors">
                  Get Started
                </Link>
              </div>

              {/* Monthly - Popular */}
              <div className="rounded-2xl border-2 border-accent p-8 bg-accent/5 relative" data-testid="biz-monthly-plan">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-accent text-neutral-900 text-xs font-bold rounded-full uppercase tracking-wide">
                  Best Value
                </div>
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className="w-5 h-5 text-accent" />
                  <span className="text-sm font-medium text-accent uppercase tracking-wide">Monthly</span>
                </div>
                <PriceTag entry={p.business_monthly} unit="/month" />
                <ul className="text-neutral-600 text-sm mb-6 space-y-2">
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-0.5 shrink-0" /> Homepage featured spotlight for 30 days</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-0.5 shrink-0" /> Top placement in directory search</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-0.5 shrink-0" /> "Featured" badge on your listing</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-accent mt-0.5 shrink-0" /> Save 35% vs. weekly pricing</li>
                </ul>
                <p className="text-xs text-neutral-500 mb-6">Best for steady, ongoing visibility and building long-term recognition in the community.</p>
                <Link to="/register" className="block w-full text-center py-3 rounded-xl bg-accent text-neutral-900 font-semibold hover:bg-accent/90 transition-colors">
                  Get Started
                </Link>
              </div>
            </div>
          </div>

          {/* Event Pricing */}
          <div>
            <div className="flex items-center justify-center gap-3 mb-3">
              <Calendar className="w-6 h-6 text-secondary" />
              <h2 className="text-2xl sm:text-3xl font-bold text-neutral-900" style={{ fontFamily: 'DM Serif Display, serif' }}>
                Featured Event Placement
              </h2>
            </div>
            <p className="text-neutral-600 text-center mb-10 max-w-lg mx-auto">
              Get your event noticed. Perfect for churches, nonprofits, festivals, and local organizers.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl mx-auto">
              {/* Weekly */}
              <div className="rounded-2xl border-2 border-neutral-200 p-8 bg-white hover:border-secondary/30 transition-colors" data-testid="evt-weekly-plan">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-5 h-5 text-neutral-400" />
                  <span className="text-sm font-medium text-neutral-500 uppercase tracking-wide">Weekly</span>
                </div>
                <PriceTag entry={p.event_weekly} unit="/week" />
                <ul className="text-neutral-600 text-sm mb-6 space-y-2">
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-0.5 shrink-0" /> Homepage featured spotlight for 7 days</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-0.5 shrink-0" /> Top placement in events listing</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-0.5 shrink-0" /> "Featured" badge on your event</li>
                </ul>
                <p className="text-xs text-neutral-500 mb-6">Great for recurring weekly events like classes, meetups, or markets.</p>
                <Link to="/register" className="block w-full text-center py-3 rounded-xl border-2 border-secondary text-secondary font-semibold hover:bg-secondary hover:text-white transition-colors">
                  Get Started
                </Link>
              </div>

              {/* Until Event Date - Popular */}
              <div className="rounded-2xl border-2 border-accent p-8 bg-accent/5 relative" data-testid="evt-flat-plan">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-accent text-neutral-900 text-xs font-bold rounded-full uppercase tracking-wide">
                  Most Popular
                </div>
                <div className="flex items-center gap-2 mb-1">
                  <Zap className="w-5 h-5 text-accent" />
                  <span className="text-sm font-medium text-accent uppercase tracking-wide">Until Event Date</span>
                </div>
                <PriceTag entry={p.event_until_date} unit="flat" />
                <ul className="text-neutral-600 text-sm mb-6 space-y-2">
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-0.5 shrink-0" /> Featured from now until your event date</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-0.5 shrink-0" /> Top placement in events listing</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-0.5 shrink-0" /> "Featured" badge on your event</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-accent mt-0.5 shrink-0" /> One simple price, no matter the duration</li>
                </ul>
                <p className="text-xs text-neutral-500 mb-6">Ideal for festivals, fundraisers, and one-time community events planned weeks in advance.</p>
                <Link to="/register" className="block w-full text-center py-3 rounded-xl bg-accent text-neutral-900 font-semibold hover:bg-accent/90 transition-colors">
                  Get Started
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Free Listing Note */}
      <section className="py-16 bg-neutral-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-2xl mx-auto bg-white rounded-2xl p-8 border border-neutral-200">
            <Zap className="w-10 h-10 text-accent mx-auto mb-4" />
            <h3 className="text-xl font-bold text-neutral-900 mb-3">Free Listings Are Always Free</h3>
            <p className="text-neutral-600 mb-6">
              Every business and event gets a free listing on Explore MHC with a full detail page, map, contact info, and social links. Featured placement is optional — it just gives you extra visibility.
            </p>
            <Link to="/register" className="btn-accent inline-block">
              List Your Business Free
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-neutral-900 mb-10" style={{ fontFamily: 'DM Serif Display, serif' }}>
            Common Questions
          </h2>
          {[
            {
              q: "How do I get a featured listing?",
              a: "Create an account, submit your business or event, and once approved, you can purchase a featured placement from your dashboard."
            },
            {
              q: "Can I cancel or change my plan?",
              a: "Featured placements run for the selected duration. You can choose not to renew when it expires. If your submission is rejected, you'll receive a full refund."
            },
            {
              q: "What happens when my featured period ends?",
              a: "Your listing stays on the site — it just moves back to its standard position in the directory. You can renew anytime from your dashboard."
            },
            {
              q: "Is there a free option?",
              a: "Absolutely! Every listing on Explore MHC is free. Featured placement is completely optional and just gives you extra visibility."
            }
          ].map((faq, i) => (
            <div key={i} className="border-b border-neutral-200 py-5 last:border-0">
              <h3 className="font-semibold text-neutral-900 mb-2">{faq.q}</h3>
              <p className="text-neutral-600 text-sm leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Pricing;
